import { Component, computed, inject, input, output, signal } from '@angular/core';
import { SimpleDialogLayout, Button, Dropzone, DropzoneFile } from 'excellence-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { Api } from '../../../../core/clients/omni-core/api';
import { uploadFile } from '../../../../core/clients/omni-core/functions';
import { FileState } from './definitions';

@Component({
  selector: 'app-document-upload-dialog',
  imports: [SimpleDialogLayout, Button, Dropzone, DropzoneFile, FileSizePipe, TranslocoPipe],
  templateUrl: './document-upload-dialog.html',
})
export class DocumentUploadDialog {
  private api = inject(Api);

  organizationID = input.required<string>();

  clickCancel = output<void>();
  uploadSuccess = output<void>();

  protected files = signal<FileState[]>([]);

  protected isUploading = computed(() => this.files().some((f) => f.isUploading));
  protected finished = computed(() => this.files().every((f) => f.finished));
  protected uploadButtonDisabled = computed(
    () => this.isUploading() || this.files().length === 0 || this.finished(),
  );

  protected onSelectFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.onDrop(files);
    }
  }

  protected onDrop(files: FileList) {
    this.files.update((current) => [
      ...current,
      ...Array.from(files).map((file) => ({
        id: crypto.randomUUID(),
        file,
        isUploading: false,
        finished: false,
        error: null,
      })),
    ]);
  }

  protected onClickRemove(fileId: string) {
    this.files.update((current) => current.filter((f) => f.id !== fileId));
  }

  protected async onClickUpload() {
    for (const file of this.files()) {
      await this.uploadFile(file);
    }
    if (this.finished()) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.uploadSuccess.emit();
      this.files.set([]);
    }
  }

  protected async onClickRetry(fileId: string) {
    const file = this.files().find((f) => f.id === fileId);
    if (!file) {
      throw new Error('File not found');
    }
    this.files.update((current) =>
      current.map((f) => (f.id === fileId ? { ...f, isUploading: true, error: null } : f)),
    );
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await this.uploadFile(file);
    if (this.finished()) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.uploadSuccess.emit();
      this.files.set([]);
    }
  }

  private async uploadFile(file: FileState) {
    try {
      this.files.update((current) =>
        current.map((f) => (f.id === file.id ? { ...f, isUploading: true } : f)),
      );
      await this.api.invoke(uploadFile, {
        'X-Filename': encodeURIComponent(file.file.name),
        oid: this.organizationID(),
        body: file.file,
      });
      this.files.update((current) =>
        current.map((f) => (f.id === file.id ? { ...f, isUploading: false, finished: true } : f)),
      );
    } catch (error) {
      this.files.update((current) =>
        current.map((f) => (f.id === file.id ? { ...f, isUploading: false, error } : f)),
      );
    }
  }
}
